import { fetchFleetOverview, fetchSites } from './api.ts';

export interface OverviewWarmSnapshot {
  sites: unknown[];
  fleetOverview: unknown[];
  refreshedAt: number;
}

type OverviewListener = (snapshot: OverviewWarmSnapshot) => void;

type OverviewWarmState = {
  snapshot: OverviewWarmSnapshot | null;
  inFlight: boolean;
  timerId: number | null;
  listeners: Set<OverviewListener>;
};

const overviewWarmState: OverviewWarmState = {
  snapshot: null,
  inFlight: false,
  timerId: null,
  listeners: new Set(),
};

async function fetchOverviewSnapshot(): Promise<OverviewWarmSnapshot> {
  const [sites, fleetOverview] = await Promise.all([
    fetchSites() as Promise<unknown[]>,
    fetchFleetOverview('vetra') as Promise<unknown[]>,
  ]);

  return {
    sites: Array.isArray(sites) ? sites : [],
    fleetOverview: Array.isArray(fleetOverview) ? fleetOverview : [],
    refreshedAt: Date.now(),
  };
}

function notifyListeners(snapshot: OverviewWarmSnapshot): void {
  overviewWarmState.listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.warn('[overviewWarmCache] listener failed:', error);
    }
  });
}

async function refreshOverviewSnapshot(): Promise<OverviewWarmSnapshot> {
  if (overviewWarmState.inFlight) {
    return overviewWarmState.snapshot || (await fetchOverviewSnapshot());
  }

  overviewWarmState.inFlight = true;
  try {
    const snapshot = await fetchOverviewSnapshot();
    overviewWarmState.snapshot = snapshot;
    notifyListeners(snapshot);
    return snapshot;
  } finally {
    overviewWarmState.inFlight = false;
  }
}

export function getOverviewWarmSnapshot(): OverviewWarmSnapshot | null {
  return overviewWarmState.snapshot;
}

export async function primeOverviewWarmSnapshot(): Promise<OverviewWarmSnapshot> {
  return refreshOverviewSnapshot();
}

export function subscribeOverviewWarmSnapshot(listener: OverviewListener): () => void {
  overviewWarmState.listeners.add(listener);
  if (overviewWarmState.snapshot) {
    listener(overviewWarmState.snapshot);
  }

  return () => {
    overviewWarmState.listeners.delete(listener);
  };
}

export function startOverviewWarmPolling(intervalMs = 10_000): void {
  if (overviewWarmState.timerId) return;

  const tick = () => {
    void refreshOverviewSnapshot().catch((error) => {
      console.warn('[overviewWarmCache] refresh failed:', error);
    });
  };

  tick();
  overviewWarmState.timerId = window.setInterval(tick, intervalMs);
}
